import * as React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { offerSelector, selectRealtyUserId } from '../../../selectors';
import { checkShouldSendAdsEventForFlatOffer } from '../../../tracking/checkShouldSendAdsEvent';
import { sendAdsEvent } from '../../../tracking/sendAdsEvent';
import { useApplicationContext } from '../../ApplicationContext';

interface IParams {
  onBannerLoad?: () => void;
  onBannerClick?: () => void;
}

export function useAdfoxBannerActions({ onBannerLoad, onBannerClick }: IParams = {}) {
  const { config } = useApplicationContext();
  const savedBannerId = React.useRef<string | null>(null);
  const offer = useSelector(offerSelector);
  const realtyUserId = useSelector(selectRealtyUserId);

  const shouldSendAdsEventForFlatOffer = React.useMemo(() => checkShouldSendAdsEventForFlatOffer(offer), [offer]);
  const shouldSendAdsEventForAnyOffer = React.useMemo(
    () => !config.get<boolean>('PostView.FilteredByFlats.Enabled'),
    [config],
  );

  const onLoad = React.useCallback(
    (bannerId: string) => {
      savedBannerId.current = bannerId;

      if (bannerId && (shouldSendAdsEventForFlatOffer || shouldSendAdsEventForAnyOffer)) {
        sendAdsEvent({
          realtyUserId,
          action: 'show',
          bannerId,
          bannerPlace: 'offerCard',
        });
      }

      if (onBannerLoad) {
        onBannerLoad();
      }
    },
    [realtyUserId, shouldSendAdsEventForAnyOffer, shouldSendAdsEventForFlatOffer, onBannerLoad],
  );

  const onClick = React.useCallback(() => {
    onBannerClick?.();

    if (savedBannerId.current && (shouldSendAdsEventForFlatOffer || shouldSendAdsEventForAnyOffer)) {
      sendAdsEvent({
        realtyUserId,
        action: 'click',
        bannerId: savedBannerId.current,
        bannerPlace: 'offerCard',
      });
    }
  }, [onBannerClick, realtyUserId, shouldSendAdsEventForAnyOffer, shouldSendAdsEventForFlatOffer]);

  return useMemo(() => ({ onClick, onLoad }), [onClick, onLoad]);
}
